// Scene 2 - Paket waehlen (Suche + Grid + "Mehr anzeigen").
// Das i auf der Kachel oeffnet die Artefaktliste MIT Deploy-Status, damit sich ein
// Paket pruefen laesst, ohne es auszuwaehlen und den Wizard weiterzuschalten.

import React, { useMemo, useRef, useState } from "react";
import ToggleButton from "@mui/material/ToggleButton";
import Skeleton from "../../../components/Skeleton";
import { useLang } from "../i18n";
import { useTransportV2 } from "../context/TransportV2Context";
import { usePackageList } from "../hooks/usePackageList";
import { api } from "../../../utils/helpers";
import { packagePolicyPrefixes, matchesPackagePolicy } from "../utils/packagePolicyFilter";
import PackageScopeSelect from "../components/PackageScopeSelect";
import PlantNamingControl, { PlantNamingField } from "../components/PlantNamingControl";
import { validPlantNaming } from "../utils/plantNaming";
import TargetNamesDialog from "../components/TargetNamesDialog";

const INITIAL_VISIBLE = 6;

function storedView(userId) {
  if (!userId) return "cards";
  try { return localStorage.getItem(`transport-package-view:${userId}`) === "list" ? "list" : "cards"; }
  catch { return "cards"; }
}

function storedScope(key) {
  try { return key && localStorage.getItem(key) === "all" ? "all" : "policy"; }
  catch { return "policy"; }
}

// 2-Buchstaben-Monogramm wie instanceMark() in der alten Transport-App,
// hier in Signal-Orange (siehe .pkg-mark).
function pkgMark(name) {
  if (!name) return "??";
  const cleaned = String(name).replace(/[^a-zA-Z]/g, " ").trim().toUpperCase();
  if (!cleaned) return "??";
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).slice(0, 2);
  return parts[0].slice(0, 2);
}

// Das Gebiet kommt als PARAMETER, nicht aus einer Modulvariablen: die Funktion
// steht ausserhalb der Komponente, ein dort abgelegter Wert froere die Sprache
// beim Import ein. Vorgabe bleibt de-DE, damit nicht angepasste Aufrufer sich
// genau wie bisher verhalten.
function formatDate(v, gebiet = "de-DE") {
  if (!v) return null;
  try {
    // SAP OData Ticks: /Date(1720000000000)/
    if (typeof v === "string" && v.startsWith("/Date(")) {
      const m = /\/Date\((\d+)\)\//.exec(v);
      if (m) return new Date(+m[1]).toLocaleDateString(gebiet);
    }
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d.toLocaleDateString(gebiet);
  } catch {}
  return null;
}

export default function Scene2Package({ userId }) {
  const { lang, t } = useLang();
  const gebiet = lang === "en" ? "en-GB" : "de-DE";
  const { state, dispatch } = useTransportV2();
  const { packages, loading, error, reload } = usePackageList(state.sourceInstanceId);
  const [q, setQ] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [selectedOnly, setSelectedOnly] = useState(false);
  const selectedIds = useMemo(() => new Set((state.selectedPackages || []).map(pkg => pkg.id)), [state.selectedPackages]);
  const [view, setView] = useState(() => storedView(userId));
  const scopeKey = userId && state.spaceId ? `transport-package-scope:${userId}:${state.spaceId}` : null;
  const [scope, setScope] = useState(() => storedScope(scopeKey));
  const prefixes = useMemo(() => packagePolicyPrefixes(state.space?.sourceInstance), [state.space?.sourceInstance]);
  const changeScope = (value) => {
    setScope(value);
    if (scopeKey) {
      try { localStorage.setItem(scopeKey, value); } catch {}
    }
  };
  const changeView = (value) => {
    setView(value);
    if (userId) {
      try { localStorage.setItem(`transport-package-view:${userId}`, value); } catch {}
    }
  };
  // Artefakt-Popup: { pkg, loading, error, rows } | null. Die Runtime-Liste (Deploy-
  // Status der GANZEN Instanz) wird einmal geholt und wiederverwendet - sie aendert
  // sich zwischen zwei Klicks in derselben Szene praktisch nie.
  const [info, setInfo] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const nextButtonRef = useRef(null);
  const closePreview = () => {
    setPreviewOpen(false);
    requestAnimationFrame(() => nextButtonRef.current?.focus());
  };
  const runtimeRef = useRef({ instId: null, map: null });

  const openInfo = async (p, e) => {
    e.stopPropagation();
    const iid = state.sourceInstanceId;
    setInfo({ pkg: p, loading: true, error: null, rows: [] });
    try {
      const flowsP = api(`/api/${iid}/content/packages/${encodeURIComponent(p.id)}/flows`, { timeout: 45000 });
      let rtP = null;
      if (runtimeRef.current.instId !== iid || !runtimeRef.current.map) {
        rtP = api(`/api/${iid}/iflows`, { timeout: 45000 }).catch(() => []);
      }
      const flows = await flowsP;
      if (rtP) {
        const rt = await rtP;
        const map = {};
        for (const r of Array.isArray(rt) ? rt : []) { if (r?.Id) map[r.Id] = r; }
        runtimeRef.current = { instId: iid, map };
      }
      const rtMap = runtimeRef.current.map || {};
      const rows = (Array.isArray(flows) ? flows : []).map((f) => {
        const rt = rtMap[f.Id];
        return {
          id: f.Id,
          name: f.Name || f.Id,
          type: f.Type || "Integration Flow",
          version: f.Version || "-",
          rtStatus: rt?.Status || null,
          rtVersion: rt?.Version || null,
        };
      }).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setInfo((cur) => (cur && cur.pkg.id === p.id ? { ...cur, loading: false, rows } : cur));
    } catch (err) {
      setInfo((cur) => (cur && cur.pkg.id === p.id ? { ...cur, loading: false, error: err?.message || String(err) } : cur));
    }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const collator = new Intl.Collator(gebiet, { numeric: true, sensitivity: "base" });
    return packages.filter((p) => selectedOnly ? selectedIds.has(p.id)
      : (scope === "all" || !prefixes.length || matchesPackagePolicy(p, prefixes))
        && (!s || p.id.toLowerCase().includes(s) || (p.name || "").toLowerCase().includes(s)))
      .sort((a, b) => collator.compare(a.name || a.id, b.name || b.id) || collator.compare(a.id, b.id));
  }, [packages, q, gebiet, scope, prefixes, selectedOnly, selectedIds]);

  const visible = selectedOnly || view === "list" || q || showAll ? filtered : filtered.slice(0, INITIAL_VISIBLE);
  const hidden = filtered.length - visible.length;

  const pick = (p) => {
    if (packageBlocked(p)) return;
    dispatch({
      type: "TOGGLE_PACKAGE",
      payload: { id: p.id, name: p.name, version: p.version, mod: p.mod },
    });
  };
  const packageBlocked = p => state.plantNaming?.mode === "existing" && state.selectedPackages?.length > 0
    && !state.selectedPackages.some(selected => selected.id === p.id);

  return (
    <section className="scene active scene-enter" id="s2">
      <div className="sh">
        <h2>{t("s2.h2")}</h2>
        <p>{t("s2.p")}</p>
      </div>

      <div className="searchrow package-searchrow">
        <div className="search-wrap">
          <svg className="ic ic-16"><use href="#ic-search" /></svg>
          <input
            className="inp"
            type="search"
            aria-label={t("s2.search")}
            placeholder={t("s2.search")}
            value={q}
            disabled={selectedOnly}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <ToggleButton className="package-selection-filter" value="selected" selected={selectedOnly}
          disabled={!selectedOnly && selectedIds.size === 0} aria-controls="s2-packages"
          title={t(selectedOnly ? "s2.selectionFilter.off" : "s2.selectionFilter.on")}
          onChange={() => setSelectedOnly(value => !value)}>
          {t("batch.selected", { n: selectedIds.size })}
        </ToggleButton>
        <div className="package-display-controls">
          {prefixes.length > 0 && <PackageScopeSelect value={scope} onChange={changeScope} prefixes={prefixes} disabled={selectedOnly} />}
          <PlantNamingControl />
          <div className="package-view-switch" role="group" aria-label={t("s2.view")}>
          {["cards", "list"].map(value => <button key={value} type="button" className="btn btn-sec"
            aria-label={t(`s2.view.${value}`)} title={t(`s2.view.${value}`)} aria-pressed={view === value}
            onClick={() => changeView(value)}>
            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              {value === "cards" ? <><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>
                : <><path d="M9 5h12M9 12h12M9 19h12"/><path d="M3 5h1M3 12h1M3 19h1"/></>}
            </svg>
          </button>)}
          </div>
        </div>
      </div>

      <PlantNamingField />

      {loading && <Skeleton variant="cards" count={8} columns={4} label={t("common.loading")} />}
      {error && (
        <div className="errline">
          <span style={{ flex: 1 }}>{t("err.packages")}: {error}</span>
          <button className="btn btn-sec btn-sm" onClick={reload}>{t("err.retry")}</button>
        </div>
      )}

      <div id="s2-packages" className={"pkg-grid" + (view === "list" ? " pkg-list" : "") + (state.pkgId ? " has-selection" : "")}>
        {visible.map((p, i) => (
          <button
            key={p.id}
            type="button"
            className={"pkg-card" + (state.selectedPackages?.some(pkg => pkg.id === p.id) ? " selected" : "") + (view === "cards" ? " stagger-in" : "")}
            aria-pressed={!!state.selectedPackages?.some(pkg => pkg.id === p.id)}
            aria-disabled={packageBlocked(p)}
            title={packageBlocked(p) ? t("naming.packageLimit") : undefined}
            style={{ "--i": i }}
            onClick={() => pick(p)}
          >
            <span className="row1">
              <span className="pkg-mark" aria-hidden="true">{pkgMark(p.name || p.id)}</span>
              <span className="pkg-name">{p.name || p.id}</span>
            </span>
            {p.name && p.name !== p.id && <span className="pkg-id">{p.id}</span>}
            {p.version && <span className="pkg-id">{t("s2.ver", { v: p.version })}</span>}
            <span className="pkg-meta">
              {p.shortText && <span>{p.shortText}</span>}
              {formatDate(p.mod, gebiet) && <span>{t("s2.mod", { d: formatDate(p.mod, gebiet) })}</span>}
            </span>
            {/* Ecklasche oben rechts (gleiche Bauart wie die CI/CD-Lasche der Space-Karten).
                span statt button: die Kachel selbst IST ein button, verschachtelte Buttons
                sind invalides HTML. Enter/Leertaste oeffnen trotzdem. */}
            <span
              role="button"
              tabIndex={0}
              className="pkg-info-btn"
              title={t("s2.infoBtn")}
              aria-label={t("s2.infoBtn")}
              onClick={(e) => openInfo(p, e)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openInfo(p, e); } }}
            >
              <svg className="ic ic-16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" />
              </svg>
            </span>
          </button>
        ))}
        {!loading && !error && filtered.length === 0 && (
          <span className="muted">{t(selectedOnly && selectedIds.size === 0 ? "s2.selectionFilter.empty" : "s2.none")}</span>
        )}
      </div>

      {hidden > 0 && (
        <div style={{ marginTop: 16 }}>
          <button className="btn btn-sec btn-sm" onClick={() => setShowAll(true)}>
            {t("s2.more", { n: hidden })}
          </button>
        </div>
      )}

      <div className="footbar">
        <button className="btn btn-ghost" onClick={() => dispatch({ type: "SET_STEP", payload: 1 })}>
          <svg className="ic ic-16"><use href="#ic-collapse" /></svg> {t("common.back")}
        </button>
        <button ref={nextButtonRef} className="btn btn-cta" onClick={() => setPreviewOpen(true)} disabled={!state.pkgId || !validPlantNaming(state.plantNaming)}>
          {t("s2.next")}
          <span className="btn-arrow"><svg className="ic"><use href="#ic-arrow-right" /></svg></span>
        </button>
      </div>

      {previewOpen && <TargetNamesDialog onClose={closePreview}
        onContinue={() => dispatch({ type: "SET_STEP", payload: 3 })} />}

      {/* Artefakt-Popup nativ als .modal-ov (kein MUI-Portal). */}
      <div
        className={"modal-ov" + (info ? " show" : "")}
        role="dialog"
        aria-modal="true"
        aria-labelledby="s2-info-title"
        onClick={(e) => { if (e.target === e.currentTarget) setInfo(null); }}
      >
        <div className="modal" style={{ maxWidth: 860 }}>
          <div className="modal-h">
            <div style={{ minWidth: 0 }}>
              <h3 id="s2-info-title" style={{ margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {info ? t("s2.infoTitle", { name: info.pkg.name || info.pkg.id }) : ""}
              </h3>
              {info?.pkg.name && info.pkg.name !== info.pkg.id && <div className="tiny muted">{info.pkg.id}</div>}
              {info && !info.loading && !info.error && (
                <span className="tiny muted">
                  {t("s2.infoSub", { n: info.rows.length, m: info.rows.filter((r) => r.rtStatus === "STARTED").length })}
                </span>
              )}
            </div>
            <button type="button" className="modal-x" aria-label={t("common.close")} onClick={() => setInfo(null)}>
              <svg className="ic ic-16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M6 6 L18 18 M18 6 L6 18" />
              </svg>
            </button>
          </div>
          <div className="modal-b">
            {info?.loading && <div className="muted" style={{ padding: "8px 0" }}>{t("s2.infoLoading")}</div>}
            {info?.error && <div className="errline"><span style={{ flex: 1 }}>{t("s2.infoErr")}: {info.error}</span></div>}
            {info && !info.loading && !info.error && info.rows.length === 0 && (
              <div className="muted" style={{ padding: "8px 0" }}>{t("s2.infoNone")}</div>
            )}
            {info && !info.loading && !info.error && info.rows.length > 0 && (
              <table className="dt dt--compact">
                <thead>
                  <tr>
                    <th>{t("th.art")}</th>
                    <th>{t("th.type")}</th>
                    <th>{t("s2.thVer")}</th>
                    <th>{t("th.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {info.rows.map((r) => {
                    // STARTED = laeuft im Ziel; jeder andere Runtime-Status (ERROR,
                    // STARTING, STOPPING) wird woertlich gezeigt statt beschoenigt.
                    const deployedOk = r.rtStatus === "STARTED";
                    const versionAbweichend = deployedOk && r.rtVersion && r.rtVersion !== r.version;
                    return (
                      <tr key={r.id}>
                        <td>
                          <span className="art-name">{r.name}</span>
                          {/* Id nur zeigen, wenn sie vom Namen abweicht - identische
                              Doppelzeilen kosteten die halbe Fensterhoehe. */}
                          {r.id !== r.name && <><br /><span className="art-id">{r.id}</span></>}
                        </td>
                        <td>{r.type}</td>
                        <td>{r.version}</td>
                        <td>
                          {deployedOk && (
                            <span className="st st--ok"><i className="dot" />
                              {versionAbweichend ? t("s2.deployedOther", { v: r.rtVersion }) : t("s2.deployed")}
                            </span>
                          )}
                          {!deployedOk && r.rtStatus && (
                            <span className="st st--crit"><i className="dot" />{r.rtStatus}</span>
                          )}
                          {!r.rtStatus && (
                            <span className="st st--idle"><i className="dot" />{t("s2.notDeployed")}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
